import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './login.scss';
import { KakaoButton } from './KakaoButton';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function BrandMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.3" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.3" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

export default function Login() {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [keepLogin, setKeepLogin] = useState(false);
  const [emailErr, setEmailErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [statusMsg, setStatusMsg] = useState(
    searchParams.get('error') === 'kakao' ? '카카오 로그인에 실패했습니다. 다시 시도해 주세요.' : ''
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = email.trim();
    const nextEmailErr = EMAIL_REGEX.test(v) ? '' : '이메일 주소를 다시 확인해 주세요.';
    const nextPasswordErr = password ? '' : '비밀번호를 입력해 주세요.';
    setEmailErr(nextEmailErr);
    setPasswordErr(nextPasswordErr);
    setStatusMsg('');
    if (nextEmailErr || nextPasswordErr) return;

    setIsLoading(true);
    try {
      await axios.post('/api/auth/login', { email: v, password, keepLogin });
      navigate('/');
    } catch (err) {
      setStatusMsg(
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : '잠시 후 다시 시도해 주세요.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">

      <header className="login-nav">
        <div className="login-nav-in">
          <Link className="brand" to="/">
            <BrandMark size={24} />
            <span className="brand-txt">
              <span className="brand-kr">클래시커</span>
              <span className="brand-lat">CLASSICKER</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="login-main">
        <div className="login-grid">

          {/* ---------------- aside ---------------- */}
          <aside className="login-aside">
            <p className="eyebrow">Welcome back</p>
            <h1>오늘의 무대를<br />프로필에 <em>더하세요</em>.</h1>
            <p className="lede">레퍼토리와 연주 이력, 이번 시즌 일정을 한 곳에서 이어서 정리합니다.</p>
            <div className="staff" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></div>
          </aside>

          {/* ---------------- form ---------------- */}
          <section className="login-card">
            <div className="card-head">
              <span>Sign in</span>
              <span>로그인</span>
            </div>

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <label className="field">
                <span className="field-label">이메일</span>
                <input
                  type="email"
                  placeholder="name@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!emailErr}
                />
                {emailErr && <span className="field-err">{emailErr}</span>}
              </label>

              <label className="field">
                <span className="field-label">비밀번호</span>
                <span className="pw-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="비밀번호"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={!!passwordErr}
                  />
                  <button
                    type="button"
                    className="pw-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    {showPassword ? '숨기기' : '보기'}
                  </button>
                </span>
                {passwordErr && <span className="field-err">{passwordErr}</span>}
              </label>

              <div className="form-row">
                <label className="check">
                  <input type="checkbox" checked={keepLogin} onChange={(e) => setKeepLogin(e.target.checked)} />
                  <span>로그인 상태 유지</span>
                </label>
                <Link className="link-quiet" to="/find-password">비밀번호 찾기</Link>
              </div>

              <button className="btn btn-primary btn-block" type="submit" disabled={isLoading}>
                {isLoading ? '로그인 중…' : '로그인'}
              </button>
              <p className="status-msg" role="status">{statusMsg}</p>
            </form>

            <div className="divider"><span>또는</span></div>
            <KakaoButton label="카카오 로그인" />

            <div className="card-foot">
              아직 계정이 없으신가요? <Link to="/signup">회원가입</Link>
            </div>
          </section>

        </div>
      </main>

      <footer className="login-foot">
        <span>© 2026 CLASSICKER</span>
        <span>
          <a href="/">이용약관</a> · <a href="/">개인정보 처리방침</a>
        </span>
      </footer>

    </div>
  );
}
